import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { api, ApiError, asList } from '../lib/api';
import { timetableApi, type Grid, type Room, type Clash } from '../lib/timetableApi';

interface Institution { id: string; name: string; }
interface AcademicClass { id: string; label: string; }
interface Subject { id: string; code: string; name: string; }
interface Period { id: string; periodNumber: number | null; startTime: string; isBreak: boolean; }
interface Teacher { id: string; displayName: string; }

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/// Timetable builder. The grid editor and the auto-generate button both hit the same clash-checked
/// backend — placing a period that would double-book a teacher/room/class (or blow a weekly load
/// limit) is refused with an explanation. Staff-gated.
export function TimetableBuilderPage() {
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [institutionId, setInstitutionId] = useState('');
  const [tab, setTab] = useState<'build' | 'rooms' | 'loads'>('build');

  useEffect(() => {
    api.get<Institution[]>('/api/institutions').then((r) => {
      setInstitutions(r);
      if (r.length === 1) setInstitutionId(r[0].id);
    }).catch(() => setInstitutions([]));
  }, []);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Academics</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Timetable builder</h1>
        <p style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 4 }}>Place periods by hand with live clash-checking, or auto-generate a first draft and adjust it.</p>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label className="label">Institution</label>
        <select className="input" value={institutionId} onChange={(e) => setInstitutionId(e.target.value)} style={{ maxWidth: 320 }}>
          <option value="">Select an institution</option>
          {institutions.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
        </select>
      </div>

      {institutionId && (
        <>
          <div className="tab-strip" style={{ gap: 4, marginBottom: 16 }}>
            <TabBtn active={tab === 'build'} onClick={() => setTab('build')}>Build</TabBtn>
            <TabBtn active={tab === 'rooms'} onClick={() => setTab('rooms')}>Rooms</TabBtn>
            <TabBtn active={tab === 'loads'} onClick={() => setTab('loads')}>Teacher loads</TabBtn>
          </div>
          {tab === 'build' && <Builder institutionId={institutionId} />}
          {tab === 'rooms' && <RoomsTab institutionId={institutionId} />}
          {tab === 'loads' && <LoadsTab />}
        </>
      )}
    </div>
  );
}

function Builder({ institutionId }: { institutionId: string }) {
  const [classes, setClasses] = useState<AcademicClass[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [classId, setClassId] = useState('');
  const [grid, setGrid] = useState<Grid | null>(null);
  const [editing, setEditing] = useState<{ day: string; period: number } | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      api.get<AcademicClass[]>(`/api/classes?institutionId=${institutionId}`),
      api.get<Subject[]>(`/api/subjects?institutionId=${institutionId}`),
      api.get<Period[]>(`/api/periods?institutionId=${institutionId}`),
      timetableApi.listTeachers(),
      timetableApi.listRooms(),
    ]).then(([c, s, p, t, r]) => { setClasses(c); setSubjects(s); setPeriods(p); setTeachers(t); setRooms(r); }).catch(() => {});
  }, [institutionId]);

  const loadGrid = useCallback((cid: string) => {
    if (!cid) { setGrid(null); return; }
    timetableApi.grid(cid).then(setGrid).catch(() => setGrid(null));
  }, []);
  useEffect(() => loadGrid(classId), [classId, loadGrid]);

  const teaching = periods.filter((p) => !p.isBreak && p.periodNumber != null);
  const subjectById = (id: string) => subjects.find((s) => s.id === id);
  const teacherById = (id: string | null) => teachers.find((t) => t.id === id);
  const roomById = (id: string | null) => rooms.find((r) => r.id === id);
  const cellAt = (day: string, period: number) => grid?.slots.find((s) => s.day === day && s.periodNumber === period) ?? null;

  const autoGen = async () => {
    if (!classId) return;
    // demand = 1 period/week per active subject as a naive default draft (staff then adjust).
    const demands = subjects.map((s) => ({ subjectId: s.id, periodsPerWeek: 1 }));
    try {
      const res = await timetableApi.autoGenerate({ academicClassId: classId, demands, overwrite: false });
      setGrid(res.grid);
      setBanner(res.complete ? `Draft generated: ${res.placed} periods placed.` : `Placed ${res.placed}; ${res.unplaced} couldn't be placed without a clash — finish those by hand.`);
    } catch (e) { setBanner(e instanceof ApiError ? e.message : 'Could not generate.'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <label className="label">Class</label>
          <select className="input" value={classId} onChange={(e) => setClassId(e.target.value)} style={{ minWidth: 220 }}>
            <option value="">Select a class</option>
            {classes.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
        {classId && <button className="btn" onClick={autoGen} style={{ marginBottom: 1 }}>⚡ Auto-generate draft</button>}
      </div>

      {banner && <div className="pill pill-muted" style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>{banner}</div>}
      {classId && subjects.length === 0 && <div className="pill pill-warn" style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12 }}>Add subjects first.</div>}

      {grid && classId && teaching.length > 0 && (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--line)' }}>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 12, color: 'var(--ink-2)' }}>Day</th>
                {teaching.map((p) => (
                  <th key={p.id} style={{ padding: '10px 8px', textAlign: 'center', fontSize: 12, color: 'var(--ink-2)' }}>
                    <div>P{p.periodNumber}</div>
                    <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', fontWeight: 400 }}>{p.startTime}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {DAYS.map((day) => (
                <tr key={day} style={{ borderBottom: '1px solid var(--line-2)' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 600, fontSize: 13 }}>{day.slice(0, 3)}</td>
                  {teaching.map((p) => {
                    const cell = cellAt(day, p.periodNumber!);
                    const subj = cell ? subjectById(cell.subjectId) : null;
                    return (
                      <td key={p.id} style={{ padding: 4 }}>
                        <button onClick={() => setEditing({ day, period: p.periodNumber! })}
                          title={cell ? `${subj?.code ?? ''} · ${teacherById(cell.teacherId)?.displayName ?? 'no teacher'} · ${roomById(cell.roomId)?.name ?? 'no room'}` : 'Free — click to assign'}
                          style={{
                            width: '100%', minWidth: 88, minHeight: 46, padding: '4px', fontSize: 12, cursor: 'pointer',
                            border: '1px solid var(--line)', borderRadius: 6, textAlign: 'center',
                            background: cell ? 'color-mix(in srgb, var(--accent,#C1272D) 12%, var(--surface,#fff))' : 'var(--surface-2,#faf9f7)', color: 'var(--ink)',
                          }}>
                          {subj ? (
                            <>
                              <div style={{ fontWeight: 600 }}>{subj.code}</div>
                              <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>{teacherById(cell!.teacherId)?.displayName?.split(' ')[0] ?? '—'}{roomById(cell!.roomId) ? ` · ${roomById(cell!.roomId)!.name}` : ''}</div>
                            </>
                          ) : <span style={{ color: 'var(--ink-3)' }}>—</span>}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && grid && (
        <CellEditor
          classId={classId} day={editing.day} period={editing.period}
          current={cellAt(editing.day, editing.period)}
          subjects={subjects} teachers={teachers} rooms={rooms}
          onClose={() => setEditing(null)}
          onSaved={(g) => { setGrid(g); setEditing(null); }}
        />
      )}
    </div>
  );
}

function CellEditor({ classId, day, period, current, subjects, teachers, rooms, onClose, onSaved }: {
  classId: string; day: string; period: number;
  current: { subjectId: string; teacherId: string | null; roomId: string | null } | null;
  subjects: Subject[]; teachers: Teacher[]; rooms: Room[];
  onClose: () => void; onSaved: (g: Grid) => void;
}) {
  const [subjectId, setSubjectId] = useState(current?.subjectId ?? '');
  const [teacherId, setTeacherId] = useState(current?.teacherId ?? '');
  const [roomId, setRoomId] = useState(current?.roomId ?? '');
  const [clashes, setClashes] = useState<Clash[]>([]);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true); setClashes([]);
    try {
      const res = await timetableApi.setCell({
        academicClassId: classId, day, periodNumber: period,
        subjectId: subjectId || null, teacherId: teacherId || null, roomId: roomId || null,
      });
      if (res.placed) onSaved(res.grid); else setClashes(res.clashes);
    } catch (e) { setClashes([{ kind: 'Error', day, periodNumber: period, message: e instanceof ApiError ? e.message : 'Could not save.' }]); }
    finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,12,18,.45)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ padding: 22, width: 420, maxWidth: '92vw' }}>
        <h2 className="h-display" style={{ fontSize: 18, marginBottom: 2 }}>{day.slice(0, 3)} · Period {period}</h2>
        <p style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 14 }}>Assign a subject, and optionally the teacher and room — conflicts are checked across all classes.</p>

        {clashes.length > 0 && (
          <div className="pill pill-danger" style={{ padding: '10px 12px', borderRadius: 8, marginBottom: 12, display: 'block' }}>
            {clashes.map((c, i) => <div key={i} style={{ fontSize: 13 }}>⚠ {c.message}</div>)}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div><label className="label">Subject</label>
            <select className="input" value={subjectId} onChange={(e) => setSubjectId(e.target.value)}>
              <option value="">— free —</option>
              {subjects.map((s) => <option key={s.id} value={s.id}>{s.code} — {s.name}</option>)}
            </select>
          </div>
          <div><label className="label">Teacher</label>
            <select className="input" value={teacherId} onChange={(e) => setTeacherId(e.target.value)} disabled={!subjectId}>
              <option value="">— none —</option>
              {teachers.map((t) => <option key={t.id} value={t.id}>{t.displayName}</option>)}
            </select>
          </div>
          <div><label className="label">Room</label>
            <select className="input" value={roomId} onChange={(e) => setRoomId(e.target.value)} disabled={!subjectId}>
              <option value="">— none —</option>
              {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-accent" disabled={busy} onClick={save} style={{ marginLeft: 'auto' }}>{busy ? 'Saving…' : subjectId ? 'Place' : 'Clear cell'}</button>
        </div>
      </div>
    </div>
  );
}

function RoomsTab({ institutionId }: { institutionId: string }) {
  const [rows, setRows] = useState<Room[]>([]);
  const [name, setName] = useState(''); const [capacity, setCapacity] = useState('');
  const load = useCallback(() => { timetableApi.listRooms().then((r) => setRows(asList(r))).catch(() => setRows([])); }, []);
  useEffect(load, [load]);
  const add = async () => {
    if (!name.trim()) return;
    await timetableApi.createRoom({ institutionId, name: name.trim(), capacity: capacity ? Number(capacity) : null });
    setName(''); setCapacity(''); load();
  };
  return (
    <div style={{ maxWidth: 520 }}>
      <div className="card" style={{ padding: 16, marginBottom: 14, display: 'flex', gap: 8 }}>
        <input className="input" placeholder="Room 204 / CS Lab 1" value={name} onChange={(e) => setName(e.target.value)} style={{ flex: 1 }} />
        <input className="input" type="number" placeholder="Cap" value={capacity} onChange={(e) => setCapacity(e.target.value)} style={{ width: 80 }} />
        <button className="btn btn-accent" disabled={!name.trim()} onClick={add}>Add</button>
      </div>
      <div className="card" style={{ overflow: 'hidden' }}>
        {rows.length === 0 ? <div style={{ padding: 20, textAlign: 'center', color: 'var(--ink-3)' }}>No rooms yet.</div> :
          rows.map((r) => (
            <div key={r.id} style={{ display: 'flex', gap: 10, padding: '10px 14px', borderBottom: '1px solid var(--line-2)' }}>
              <strong>{r.name}</strong>
              {r.capacity != null && <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>cap {r.capacity}</span>}
            </div>
          ))}
      </div>
    </div>
  );
}

function LoadsTab() {
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [loads, setLoads] = useState<Record<string, number>>({});
  const load = useCallback(() => {
    Promise.all([timetableApi.listTeachers(), timetableApi.listLoads()]).then(([t, l]) => {
      setTeachers(t);
      setLoads(Object.fromEntries(l.map((x) => [x.teacherId, x.maxPeriodsPerWeek])));
    }).catch(() => {});
  }, []);
  useEffect(load, [load]);
  const save = async (teacherId: string, max: number) => {
    await timetableApi.setLoad({ teacherId, maxPeriodsPerWeek: max });
    setLoads((prev) => ({ ...prev, [teacherId]: max }));
  };
  return (
    <div style={{ maxWidth: 520 }}>
      <p style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 12 }}>A teacher's max periods per week. The builder blocks any placement that would exceed it.</p>
      <div className="card" style={{ overflow: 'hidden' }}>
        {teachers.length === 0 ? <div style={{ padding: 20, textAlign: 'center', color: 'var(--ink-3)' }}>No teachers yet. Invite staff with the Teacher role first.</div> :
          teachers.map((t) => (
            <div key={t.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid var(--line-2)' }}>
              <span>{t.displayName}</span>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
                <input className="input" type="number" defaultValue={loads[t.id] ?? ''} placeholder="max/wk" style={{ width: 90 }}
                  onBlur={(e) => { const v = Number(e.target.value); if (v > 0) save(t.id, v); }} />
                <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>/wk</span>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return <button onClick={onClick} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px 14px', fontSize: 14, color: active ? 'var(--ink)' : 'var(--ink-3)', fontWeight: active ? 600 : 400, borderBottom: active ? '2px solid var(--accent,#C1272D)' : '2px solid transparent' }}>{children}</button>;
}
